import { useEffect, useState } from "react";

/**
 * Reveals every `[data-reveal]` element once it scrolls into view.
 *
 * Driven by scroll/resize rather than IntersectionObserver: the elements start
 * at `opacity: 0`, so a mechanism that can silently fail to fire would leave
 * sections permanently invisible. A plain scroll listener always fires.
 */
export const useScrollReveal = () => {
  useEffect(() => {
    const pending = new Set(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    const revealAll = () => {
      pending.forEach((node) => node.classList.add("is-revealed"));
      pending.clear();
    };

    // Tells the failsafe in index.html that the app booted and will handle
    // revealing, so it should leave the `js-reveal` class in place.
    const root = document.documentElement;
    root.setAttribute("data-reveal-ready", "");

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      revealAll();
      return;
    }

    const revealVisible = () => {
      pending.forEach((node) => {
        const rect = node.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.93 && rect.bottom > 0) {
          node.classList.add("is-revealed");
          pending.delete(node);
        }
      });

      if (pending.size === 0) {
        stop();
      }
    };

    const stop = () => {
      window.removeEventListener("scroll", revealVisible);
      window.removeEventListener("resize", revealVisible);
    };

    revealVisible();
    window.addEventListener("scroll", revealVisible, { passive: true });
    window.addEventListener("resize", revealVisible);

    return stop;
  }, []);
};

/** True once the reader has scrolled roughly past the hero. */
export const useScrolledPastHero = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > window.innerHeight * 0.72);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return scrolled;
};

/** Tracks which section is currently under the reader, for nav highlighting. */
export const useActiveSection = (ids: readonly string[]) => {
  const [active, setActive] = useState(ids[0] ?? "");

  useEffect(() => {
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((node): node is HTMLElement => node !== null);

    if (sections.length === 0) {
      return;
    }

    const onScroll = () => {
      const line = window.scrollY + window.innerHeight * 0.32;
      let current = sections[0].id;

      sections.forEach((section) => {
        if (section.offsetTop <= line) {
          current = section.id;
        }
      });

      setActive(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ids]);

  return active;
};
